/**
 * Layout which includes a bunch of regions:
 * - Full width page header (under the nav menu)
 * - Content column header
 * - Left sidebar
 * - Main content (children)
 * 
 * The layout provides default implementations for each of these regions. To leverage these implementations you must pass in:
 * - title (Technically optional but really strongly recommended, for the page title in the header)
 * - nodeID (Optional, for the breadcrumbs and sidebar nav menu)
 * - keywords (Optional, for the seo component in the header)
 * - images (Optional, for the page header)
 * 
 * Eg:  
 *  <SidebarLayout nodeID={nodeID} title={title} keywords={keywords} images={imageData}>
 *      Hello world!
 *  </SidebarLayout>
 * 
 * Alternatively, you can pass in your own JSX elements to use as the content for any of these regions:
 *  - children: the main content column
 *  - header: the page header
 *  - contentHead: the content column header
 *  - sidebar: the left sidebar
 * 
 * Eg: 
 *  const sidebar = <>Sidebar content</>;
 *  const header = <>Header content</>;
 *  const contentHead = <>Content head content</>;
 * 
 *  <SidebarLayout header={header} contentHead={contentHead} sidebar={sidebar}>
 *      Hello world!
 *  </SidebarLayout>
 */

import React from 'react'
import PropTypes from 'prop-types'
import DateModified from 'src/components/dateModified'
import { Helmet } from 'react-helmet'

import Hero from 'src/uglib_components/hero'

import { contentExists } from 'src/utils/ug-utils'

import 'src/styles/global.css'
import 'src/uglib_styles/uglib.css'
import 'src/uglib_styles/tables.css'

import Seo from 'src/uglib_components/seo'
import Breadcrumbs from 'src/uglib_components/breadcrumbs'


import Sidebar from 'src/uglib_components/sidebar'
import SidebarMenu from 'src/uglib_components/sidebarMenu'

import ServiceDisruptionBar from 'src/uglib_components/serviceDisruptionBar'
import Notices from 'src/uglib_components/notices'
import BuildingHoursNotice from 'src/uglib_components/buildingHoursNotice'
import BuildingCapacityNotice from 'src/uglib_components/buildingCapacityNotice'
import A11yMessage from 'src/uglib_components/a11yMessage'
import HeaderMenu from 'src/uglib_components/headerMenu'
import ScavengerHuntBlock from 'src/uglib_components/scavengerHuntBlock'
import Banners from 'src/uglib_components/banners'

import Footer from './footer'

import AskChatTab from './askChatTab'


const SidebarLayout = ({title, header, keywords, bodyClass, contentHead, sidebar, nodeID, path, breadcrumbs, children, date, images, applyStyles}) => {
    const defaultSidebar = <>
		<Sidebar>
			<SidebarMenu url={path}/>
		</Sidebar>
	</>;

	const defaultContentHead = <>
		<ServiceDisruptionBar />
		<Banners />
		<Notices>
			<BuildingHoursNotice />
			<BuildingCapacityNotice />
		</Notices>
		<ScavengerHuntBlock />
	</>;

	const defaultHeader = <>
		<Helmet bodyAttributes={{
            class: bodyClass
        }}
        />

		<Helmet><script defer type="text/javascript" src="/assets/uglib_embeds.js"></script></Helmet>
		<Helmet><script defer type="text/javascript" src="/assets/uog-media-player.js"></script></Helmet>
		<Seo title={title} keywords={keywords} />

		{ /**** Header and Title ****/ }
		<div className={!contentExists(images) && "no-thumb"} id="rotator">
			<Hero imgData={images} />				
			<div className="container ft-container">
				<h1 className="fancy-title">{title}</h1>
			</div>
		</div>

		<Breadcrumbs nodeID={nodeID} nodeTitle={title} links={breadcrumbs} />

	</>;
    
	/*
	 * The "site-content" class applies a bunch of style rules on the content.
	 * Sometimes we don't want that to happen, so we can use the applyStyles prop to conditionally add this class
	 **/
	let content_classes = ['row', 'row-with-vspace']
	if (applyStyles) {
		content_classes.push('site-content');
	}

    return (<>
		<HeaderMenu menuName="main" />
        {contentExists(header) ? header : defaultHeader}

        <main id="content" className="main-container">
            
            <div className="container page-container">
				{contentExists(contentHead) ? contentHead : defaultContentHead}
                <div className={content_classes.join(' ')}>
                    
                    {contentExists(sidebar) ? sidebar : defaultSidebar}

                    <section className="col-md-9 content-area">
						<DateModified date={date}/>
                        {children}
                    </section>
                </div>
            </div>

			<div className="container col col-12">
				<A11yMessage />
			</div>

			<AskChatTab />
        </main>
		
		<Footer />
    </>);
}

SidebarLayout.propTypes = {
	title: PropTypes.string,
	keywords: PropTypes.array,
	bodyClass: PropTypes.string,
	header: PropTypes.node,
	contentHead: PropTypes.node,
	sidebar: PropTypes.node,
	nodeID: PropTypes.number,
	path: PropTypes.string,
	breadcrumbs: PropTypes.array,
	images: PropTypes.array,
	applyStyles: PropTypes.bool,
	date: PropTypes.string,
	children: PropTypes.node.isRequired,
}

SidebarLayout.defaultProps = {
	applyStyles: true,
	keywords: [],
    bodyClass: "",
	date: "",
	nodeID: null,
	path: "",
	breadcrumbs: [],
}

export default SidebarLayout
